import React from 'react';
import { useDatumStore } from '../../stores/datumStore';
import { useAuthStore } from '../../stores/authStore';
import { Card } from '../common/Card';

interface GenericWidgetProps {
  datumId: string;
  tenant: string;
  isPinned: boolean;
  onPinToggle: () => void;
}

export function GenericWidget({ datumId: signalId, tenant, isPinned, onPinToggle }: GenericWidgetProps) {
  const getSignal = useDatumStore((state) => state.getDatum);
  const user = useAuthStore((state) => state.user);
  const signal = getSignal(signalId);

  if (!signal || !user) return null;

  // Get latest entry for current user
  const myDatumUsers = signal.datumUsers
    .filter((du) => du.userId === user.id)
    .sort((a, b) => b.createdAt - a.createdAt);
  const latestDatumUser = myDatumUsers[0];
  const userData = latestDatumUser?.data || null;

  const kindLabels: Record<string, string> = {
    goal: 'Goal Tracker',
    habit: 'Habit Tracker',
    checklist: 'Checklist',
    poll: 'Poll',
    reminder: 'Reminder',
  };

  const kindColors: Record<string, { bg: string; border: string; text: string }> = {
    goal: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900' },
    habit: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-900' },
    checklist: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900' },
    poll: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-900' },
    reminder: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900' },
  };

  const label = kindLabels[signal.kind] || signal.kind;
  const colors = kindColors[signal.kind] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-900' };

  return (
    <Card>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 capitalize">{label}</h3>
            <p className="text-sm text-gray-500">
              {signal.accessMode === 'read_write' ? 'Interactive widget' : 'View only'}
            </p>
          </div>
          <button
            onClick={onPinToggle}
            className="p-1 hover:bg-gray-100 rounded"
            title={isPinned ? 'Unpin' : 'Pin'}
          >
            <svg
              className={`w-5 h-5 ${isPinned ? 'text-primary-600' : 'text-gray-400'}`}
              fill={isPinned ? 'currentColor' : 'none'}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
          </button>
        </div>

        {/* Fixed Data (Coach Instructions) */}
        {signal.fixedData && Object.keys(signal.fixedData).length > 0 && (
          <div className={`p-4 ${colors.bg} border ${colors.border} rounded-lg`}>
            <p className={`text-sm font-medium ${colors.text} mb-2`}>Instructions</p>
            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
              {JSON.stringify(signal.fixedData, null, 2)}
            </pre>
          </div>
        )}

        {/* User Data */}
        {userData && Object.keys(userData).length > 0 ? (
          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Your Data</p>
            <pre className="text-sm text-gray-900 whitespace-pre-wrap font-sans">
              {JSON.stringify(userData, null, 2)}
            </pre>
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">
            {signal.accessMode === 'read_write'
              ? 'No data yet. Widget implementation coming soon.'
              : 'Waiting for coach to add data.'}
          </p>
        )}

        {/* Placeholder message */}
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-xs text-yellow-800">
            <span className="font-medium">Note:</span> Full {label} widget implementation coming soon.
            This is a placeholder showing raw signal data.
          </p>
        </div>
      </div>
    </Card>
  );
}
